import React, { useState, useEffect, useMemo } from 'react';
import './PurchaseHistory.css';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  Calendar,
  Search,
  Laptop,
  Smartphone,
  Watch,
  Headphones,
  Sparkles,
} from 'lucide-react';
import en from '../locales/eng.json';
import th from '../locales/th.json';
import CONFIG from '../config';

function PurchaseHistory() {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState({
    total_spend: 0,
    total_orders: 0,
    total_points_earned: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Top bar — match MyCoupon page (logo + lang switcher + avatar/menu)
  const [userData, setUserData] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'TH';
  });
  const [t, setT] = useState(language === 'EN' ? en : th);

  useEffect(() => {
    localStorage.setItem('language', language);
    setT(language === 'EN' ? en : th);
  }, [language]);

  // Fetch user data for the avatar / member ID (mirrors MyCoupon)
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const lineUserId = localStorage.getItem('line_user_id');
        if (!lineUserId) return;
        const res = await fetch(
          `${CONFIG.API_BASE_URL}/lineuser/fetch_user_data?line_user_id=${lineUserId}`
        );
        const result = await res.json();
        if (result?.content?.data) {
          setUserData(result.content.data);
        }
      } catch (e) {
        console.error('Failed to fetch user data:', e);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const lineUserId = localStorage.getItem('line_user_id');
    if (!lineUserId) {
      navigate('/register');
      return;
    }

    const fetchHistory = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${CONFIG.API_BASE_URL}/customer_billing/purchase_history_by_line_user?line_user_id=${lineUserId}`
        );
        const result = await res.json();
        if (result && Array.isArray(result.purchases)) {
          setPurchases(result.purchases);
          setSummary({
            total_spend: Number(result.total_spend || 0),
            total_orders: Number(result.total_orders || 0),
            total_points_earned: Number(result.total_points_earned || 0),
          });
        }
      } catch (err) {
        console.error('Failed to fetch purchase history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [navigate]);

  const labels = {
    headerTitle: language === 'EN' ? 'Purchase History' : 'ประวัติการซื้อสินค้า',
    analyticsHeader:
      language === 'EN' ? 'Purchase Analytics Breakdown' : 'สรุปยอดประวัติการซื้อ',
    totalSpendLabel: language === 'EN' ? 'Total Money Spent' : 'ยอดเงินซื้อสะสมรวม',
    spendUnit: language === 'EN' ? 'THB' : 'บาท',
    itemsCount: language === 'EN' ? 'Total Invoices' : 'จำนวนออเดอร์',
    pointsClaimed: language === 'EN' ? 'Points Accrued' : 'คะแนนสะสมที่ได้รับ',
    pointsUnit: language === 'EN' ? 'pts' : 'คะแนน',
    searchPlaceholder: language === 'EN' ? 'Search products...' : 'ค้นหาสินค้า...',
    noResults:
      language === 'EN'
        ? 'No purchase history matching your search.'
        : 'ไม่พบประวัติการสั่งซื้อ',
    branchLabel: language === 'EN' ? 'Store Branch:' : 'สาขาที่ซื้อ:',
    purchaseDate: language === 'EN' ? 'Purchase Date' : 'วันที่ซื้อ',
    allLabel: language === 'EN' ? 'All' : 'ทั้งหมด',
    loadingText: language === 'EN' ? 'Loading...' : 'กำลังโหลด...',
  };

  const categories = useMemo(() => {
    const set = new Set();
    purchases.forEach((p) => {
      if (p.category) set.add(p.category);
    });
    return ['All', ...Array.from(set)];
  }, [purchases]);

  const filteredPurchases = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return purchases.filter((p) => {
      const matchCat =
        selectedCategory === 'All' || (p.category || '') === selectedCategory;
      if (!matchCat) return false;
      if (!term) return true;
      const haystack = [
        p.subcategory,
        p.category,
        p.brand,
        p.branch,
        p.invbill,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [purchases, searchTerm, selectedCategory]);

  const getCategoryIcon = (category) => {
    const c = (category || '').toLowerCase();
    if (c.includes('mac') || c.includes('laptop') || c.includes('notebook')) {
      return <Laptop className="ph-cat-icon" />;
    }
    if (c.includes('phone') || c.includes('iphone') || c.includes('โทรศัพท์')) {
      return <Smartphone className="ph-cat-icon" />;
    }
    if (c.includes('watch') || c.includes('นาฬิกา')) {
      return <Watch className="ph-cat-icon" />;
    }
    if (c.includes('audio') || c.includes('headphone') || c.includes('หูฟัง')) {
      return <Headphones className="ph-cat-icon" />;
    }
    return <ShoppingBag className="ph-cat-icon" />;
  };

  const formatPrice = (n) => {
    const value = Number(n || 0);
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="purchase-history-page">
      {/* Top header — matches MyCoupon: gradient bar + back-button bar */}
      <div className="point-header-gradient">
        <img
          onClick={() => navigate('/')}
          src="/assets/logo-ujoy.png"
          alt="logo"
          className="logo-ujoy"
        />
        <div className="right-section">
          <div className="lang-selector">
            <button
              className={`lang-button ${language === 'TH' ? 'active' : ''}`}
              onClick={() => setLanguage('TH')}
            >
              TH
            </button>
            |
            <button
              className={`lang-button ${language === 'EN' ? 'active' : ''}`}
              onClick={() => setLanguage('EN')}
            >
              EN
            </button>
          </div>

          {showMenu ? (
            <div>
              {showMenu && (
                <>
                  <img
                    onClick={() => navigate('/profile')}
                    src="/assets/mascot-full.png"
                    alt="avatar"
                    style={{ position: 'relative', zIndex: 9998 }}
                  />
                  <div className="menu-overlay" onClick={() => setShowMenu(false)}></div>
                  <div className="menu-popup-wrapper">
                    <div className="menu-popup">
                      <div
                        className="menu-item"
                        onClick={() => {
                          navigate('/my-coupon');
                          setShowMenu(false);
                        }}
                      >
                        <img src="/assets/icon-coupon.png" alt="coupon" />
                        <span>คูปองของฉัน</span>
                      </div>
                      <div
                        className="menu-item"
                        onClick={() => {
                          navigate('/point-redeem');
                          setShowMenu(false);
                        }}
                      >
                        <img src="/assets/icon-redeem.png" alt="redeem" />
                        <span>แลกคะแนน</span>
                      </div>
                      <hr className="dashed-line" />
                      <div
                        className="menu-item"
                        onClick={() => {
                          navigate('/point-history');
                          setShowMenu(false);
                        }}
                      >
                        <img src="/assets/icon-points.png" alt="points" />
                        <span>คะแนนสะสม</span>
                      </div>
                      <hr className="dashed-line" />
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div>
              <img
                onClick={() => navigate('/profile')}
                src={userData?.line_image_url ? userData.line_image_url : '/assets/avartar.png'}
                alt="avatar"
                className="profile-avatar"
              />
            </div>
          )}
        </div>
      </div>

      <div className="point-header-bar">
        <button className="back-button" onClick={() => navigate(-1)}>
          <img src="/assets/arrow_back.png" alt="Back" className="back-icon" />
        </button>
        <span className="point-header-title">
          {t?.mainmenu?.purchase_history || labels.headerTitle}
        </span>
      </div>

      <div className="ph-container">
        {/* Analytics Dashboard Card */}
        <div className="ph-analytics-card" id="purchase-analytics-dashboard">
          <div className="ph-analytics-header">
            <span>
              <Sparkles className="ph-analytics-spark" />
              {labels.analyticsHeader}
            </span>
          </div>

          <div className="ph-analytics-total">
            <span className="ph-analytics-total-label">{labels.totalSpendLabel}</span>
            <div className="ph-analytics-total-row">
              <span className="ph-analytics-total-amount">
                ฿{formatPrice(summary.total_spend)}
              </span>
              <span className="ph-analytics-total-unit">{labels.spendUnit}</span>
            </div>
          </div>

          <div className="ph-analytics-grid">
            <div className="ph-analytics-tile">
              <span className="ph-analytics-tile-label">{labels.itemsCount}</span>
              <span className="ph-analytics-tile-value">{summary.total_orders}</span>
            </div>
            <div className="ph-analytics-tile">
              <span className="ph-analytics-tile-label">{labels.pointsClaimed}</span>
              <span className="ph-analytics-tile-value">
                +{summary.total_points_earned} {labels.pointsUnit}
              </span>
            </div>
          </div>
        </div>

        {/* Filter controls */}
        <div className="ph-filters">
          <div className="ph-search-wrapper">
            <Search className="ph-search-icon" />
            <input
              id="purchase-search-input"
              type="text"
              placeholder={labels.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ph-search-input"
            />
          </div>

          {categories.length > 1 && (
            <div className="ph-category-bar" id="category-filter-bar">
              {categories.map((cat) => (
                <button
                  key={cat}
                  id={`filter-cat-${cat}`}
                  onClick={() => setSelectedCategory(cat)}
                  className={`ph-category-pill ${
                    selectedCategory === cat ? 'active' : ''
                  }`}
                >
                  {cat === 'All' ? labels.allLabel : cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* List */}
        <div className="ph-list" id="purchases-list-container">
          {loading ? (
            <div className="ph-empty">
              <p>{labels.loadingText}</p>
            </div>
          ) : filteredPurchases.length === 0 ? (
            <div className="ph-empty" id="empty-purchases-search">
              <ShoppingBag className="ph-empty-icon" />
              <p>{labels.noResults}</p>
            </div>
          ) : (
            filteredPurchases.map((p) => (
              <div
                id={`purchase-card-${p.id || p.invbill}`}
                key={p.id || p.invbill}
                className="ph-card"
              >
                <div className="ph-card-top">
                  <div className="ph-card-left">
                    <div className="ph-card-icon-wrap">{getCategoryIcon(p.category)}</div>
                    <div className="ph-card-info">
                      <div className="ph-card-tags">
                        <span className="ph-card-tag">{p.category || '-'}</span>
                        {p.points_earned > 0 && (
                          <span className="ph-card-points">
                            +{p.points_earned} 🪙
                          </span>
                        )}
                      </div>
                      <h4 className="ph-card-title">
                        {p.subcategory || p.brand || p.category || '-'}
                      </h4>
                    </div>
                  </div>
                  <div className="ph-card-price">
                    <span className="ph-card-price-amount">฿{formatPrice(p.price)}</span>
                  </div>
                </div>

                <div className="ph-card-details">
                  <div className="ph-card-detail-row">
                    <span className="ph-card-detail-label">
                      <Calendar className="ph-detail-icon" />
                      {labels.purchaseDate}
                    </span>
                    <span className="ph-card-detail-value mono">{p.date || '-'}</span>
                  </div>
                  <div className="ph-card-detail-row ph-card-detail-row-border">
                    <span className="ph-card-detail-label">{labels.branchLabel}</span>
                    <span className="ph-card-detail-value">{p.branch || '-'}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default PurchaseHistory;
